import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  ObservableMap,
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
  toJS,
} from "mobx";
import { Uuid } from "@/domains/global/identifiers";
import { BaseError } from "@/domains/errors";
import { objectModule } from "@/modules/object";
import { type ApiSchema, api } from "@/modules/api";
import {
  PlatformAccountState,
  type PlatformProactiveTrialEligibility,
  PlatformStripeSubscriptionStatus,
} from "@/store/platform-info/types";
import { PlatformStripeSubscriptionInfoObservable } from "@/store/platform-info/PlatformStripeSubscriptionInfoObservable";
import { PlatformOnboardingInfoObservable } from "@/store/platform-info/PlatformOnboardingInfoObservable";
import pRetry from "p-retry";
import {
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
  buildAsyncData,
} from "@/domains/async/AsyncData";
import { logger } from "@/modules/logger";
import {
  GatedFeature,
  GatedFeatureConfig,
  GatedFeatureConfigMap,
  GatedFeatureConfigMapData,
} from "@/store/feature-gates/types";
import { CacheKey } from "@/modules/cache/constants";
import {
  buildGatedFeatureConfigMapFromData,
  buildSerializableGatedFeatureConfigMap,
} from "@/store/feature-gates/utils";
import { PerDeviceOnboardingInfoObservable } from "@/store/platform-info/PerDeviceOnboardingInfoObservable";
import {
  type BillingSubscriptionData,
  type PlatformEntitlementsData,
  SubscriptionPlanKind,
} from "@/store/subscriptions/types";
import { clientEnvModule } from "@/modules/client-env";
import { posthogClientManager } from "@/manager/posthog-client";
import { localStorageModule } from "@/modules/local-storage";
import {
  parseCommercialCatalog,
  parseCommercialPlanSnapshot,
  type CommercialCatalogSnapshot,
  type CommercialPlanSnapshot,
} from "@/modules/commercial-plans";

interface FetchPlatformInfoOptions {
  reconcileStripeSubscription?: boolean;
}

export class AppStorePlatformInfoStore extends AppSubStore {
  fetchPlatformInfoState = buildAsyncData<boolean>({});

  accountState: PlatformAccountState;
  perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
  onboardingInfo: PlatformOnboardingInfoObservable;
  subscription: BillingSubscriptionData | null;
  entitlements: PlatformEntitlementsData | null;
  commercialCatalog: CommercialCatalogSnapshot | null;
  commercialPlanSnapshot: CommercialPlanSnapshot | null;
  proactiveTrialEligibility: PlatformProactiveTrialEligibility | undefined;
  stripeSubscriptionMap: ObservableMap<Uuid, PlatformStripeSubscriptionInfoObservable>;
  featureGateConfigs: ObservableMap<GatedFeature, GatedFeatureConfig>;
  private platformInfoInvalidationRefreshPromise: Promise<void> | null = null;
  private platformInfoInvalidationRefreshQueued = false;
  private commercialCatalogEnrollmentAttempted = false;
  private commercialCatalogEnrollmentPromise: Promise<void> | null = null;

  subscribeNowInterstitialIsOpen: boolean;
  subscribeNowInterstitialOpenedManually: boolean;
  subscriptionWasCompletedSuccessfully: boolean;
  completedSubscriptionPlanKind: SubscriptionPlanKind | null;
  welcomeToProModalIsOpen: boolean;
  welcomeToProactiveModalIsOpen: boolean;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.accountState = PlatformAccountState.Initial;

    this.stripeSubscriptionMap = new ObservableMap();

    this.perDeviceOnboardingInfo = new PerDeviceOnboardingInfoObservable({
      store: this.store,
    });

    /**
     * Note that we pass in `perDeviceOnboardingInfo` as an arg, because we want
     * to manage the lifecycle independently of the `PlatformOnboardingInfoObservable`.
     */
    this.onboardingInfo = new PlatformOnboardingInfoObservable({
      shouldShowIcpRfi: false,
      shouldShowHeadsUpCoachMark: false,
      completedTaskIds: [],
      store: this.store,
      perDeviceOnboardingInfo: this.perDeviceOnboardingInfo,
    });
    this.subscription = null;
    this.entitlements = null;
    this.commercialCatalog = null;
    this.commercialPlanSnapshot = null;
    this.proactiveTrialEligibility = undefined;

    this.subscribeNowInterstitialIsOpen = false;
    this.subscribeNowInterstitialOpenedManually = false;
    this.subscriptionWasCompletedSuccessfully = false;
    this.completedSubscriptionPlanKind = null;
    this.welcomeToProModalIsOpen = false;
    this.welcomeToProactiveModalIsOpen = false;
    this.featureGateConfigs = new ObservableMap();

    makeObservable<
      this,
      | "platformInfoInvalidationRefreshPromise"
      | "platformInfoInvalidationRefreshQueued"
      | "commercialCatalogEnrollmentAttempted"
      | "commercialCatalogEnrollmentPromise"
      | "attemptCommercialCatalogEnrollmentOnce"
      | "flushPlatformInfoInvalidationRefreshes"
    >(this, {
      dismissIcpRfi: true,
      dismissHeadsUpCoachMark: true,
      assignIntentOnboardingIfEligible: false,
      setIntentOnboardingIntent: false,
      completeIntentOnboarding: false,
      platformInfoInvalidationRefreshPromise: false,
      platformInfoInvalidationRefreshQueued: false,
      commercialCatalogEnrollmentAttempted: false,
      commercialCatalogEnrollmentPromise: false,
      fetchPlatformInfoState: observable,
      accountState: observable,
      perDeviceOnboardingInfo: observable,
      onboardingInfo: observable,
      subscription: observable.ref,
      entitlements: observable.ref,
      commercialCatalog: observable.ref,
      commercialPlanSnapshot: observable.ref,
      proactiveTrialEligibility: observable.ref,
      stripeSubscriptionMap: observable,
      featureGateConfigs: observable,
      subscribeNowInterstitialIsOpen: observable,
      subscribeNowInterstitialOpenedManually: observable,
      subscriptionWasCompletedSuccessfully: observable,
      completedSubscriptionPlanKind: observable,
      welcomeToProModalIsOpen: observable,
      welcomeToProactiveModalIsOpen: observable,
      shouldShowStripeSubscriptionInterstitial: computed,

      setSubscriptionCompletedSuccessfully: action,
      openSubscribeNowInterstitial: action,
      dismissStripeSubscriptionInterstitial: action,
      openWelcomeToProModal: action,
      closeWelcomeToProModal: action,
      openWelcomeToProactiveModal: action,
      closeWelcomeToProactiveModal: action,
      fetchPlatformInfo: action,
      refreshPlatformInfoAfterInvalidation: action,
      updateSubscriptionFromRemoteData: action,
      updateEntitlementsFromRemoteData: action,
      updateOnboardingInfoFromRemoteData: action,
      updateFeatureGateConfigsFromRemoteData: action,
      persistBillingSubscriptionToCache: action,
      persistEntitlementsToCache: action,
      hydrateBillingSubscriptionFromCache: action,
      hydrateEntitlementsFromCache: action,
      hydrateFeatureGateConfigsFromCache: action,
      initialize: action,
      getFeatureGateConfig: false,
      attemptCommercialCatalogEnrollmentOnce: false,
      flushPlatformInfoInvalidationRefreshes: false,
    });
  }

  get shouldShowStripeSubscriptionInterstitial() {
    return this.subscribeNowInterstitialIsOpen;
  }

  setSubscriptionCompletedSuccessfully = (
    planKind: SubscriptionPlanKind = SubscriptionPlanKind.PRO
  ) => {
    this.subscriptionWasCompletedSuccessfully = true;
    this.completedSubscriptionPlanKind = planKind;
  };

  openWelcomeToProModal = () => {
    this.welcomeToProModalIsOpen = true;
  };

  closeWelcomeToProModal = () => {
    this.welcomeToProModalIsOpen = false;
  };

  openWelcomeToProactiveModal = () => {
    this.welcomeToProactiveModalIsOpen = true;
  };

  closeWelcomeToProactiveModal = () => {
    this.welcomeToProactiveModalIsOpen = false;
  };

  openSubscribeNowInterstitial = () => {
    this.subscribeNowInterstitialIsOpen = true;
    this.subscribeNowInterstitialOpenedManually = true;
  };

  dismissStripeSubscriptionInterstitial = () => {
    this.subscribeNowInterstitialIsOpen = false;
    this.subscribeNowInterstitialOpenedManually = false;
  };

  async fetchPlatformInfo(options: FetchPlatformInfoOptions = {}) {
    let didFetchPlatformInfo = false;
    runInAction(() => {
      this.fetchPlatformInfoState = asyncDataLoading({});
    });

    try {
      // A billing return can race with provider reconciliation. Let the
      // verified paid subscription settle before considering Free enrollment.
      if (!options.reconcileStripeSubscription) {
        await this.attemptCommercialCatalogEnrollmentOnce();
      }

      const response = await api.get("/v2/me/platform-info", {
        ...(options.reconcileStripeSubscription
          ? { params: { query: { reconcile_stripe_subscription: "1" } } }
          : {}),
      });

      if (response.data) {
        didFetchPlatformInfo = true;
        const responseData = response.data;

        runInAction(() => {
          this.accountState = responseData.account_state as PlatformAccountState;

          // TODO: For right now, we're disabling the other onboarding stuff besides the import CTA
          responseData.onboarding_info.onboarding_state = "ONBOARDING_COMPLETED";
          responseData.onboarding_info.onboarding_profile = {};
          this.updateOnboardingInfoFromRemoteData({
            data: responseData.onboarding_info,
          });
          this.updateSubscriptionFromRemoteData({
            data: responseData.subscription,
          });
          this.proactiveTrialEligibility = responseData.proactive_trial_eligibility;

          this.commercialCatalog = parseCommercialCatalog(responseData.commercial_catalog);
          this.commercialPlanSnapshot = parseCommercialPlanSnapshot(
            responseData.commercial_plan_snapshot
          );
          if (this.commercialPlanSnapshot) {
            this.commercialPlanSnapshot = {
              ...this.commercialPlanSnapshot,
              isTrial:
                responseData.subscription.provider === "STRIPE" &&
                responseData.stripe_subscriptions.some(
                  subscription =>
                    subscription.id === responseData.subscription.provider_subscription_id &&
                    subscription.stripe_subscription_status === "TRIALING"
                ),
            };
          }

          if (responseData.entitlements) {
            this.updateEntitlementsFromRemoteData({
              data: responseData.entitlements,
            });
          }

          const subscriptions = responseData.stripe_subscriptions;
          this.stripeSubscriptionMap.clear();

          for (const subscriptionInfo of subscriptions) {
            const subscriptionObservable = new PlatformStripeSubscriptionInfoObservable({
              id: subscriptionInfo.id,
              stripeSubscriptionId: subscriptionInfo.stripe_subscription_id,
              stripeSubscriptionStatus:
                subscriptionInfo.stripe_subscription_status as PlatformStripeSubscriptionStatus,
            });

            this.stripeSubscriptionMap.set(subscriptionObservable.id, subscriptionObservable);
          }

          // Feature gate configs for monetization
          this.updateFeatureGateConfigsFromRemoteData({
            data: (responseData.feature_gate_configs as GatedFeatureConfigMapData) ?? {},
          });
        });

        runInAction(() => {
          this.fetchPlatformInfoState = asyncDataLoaded(this.fetchPlatformInfoState, true);
        });
      } else {
        logger.error({
          message: "Failed to fetch platform info for current account.",
          info: { result: objectModule.safeAsJson(response) },
        });

        runInAction(() => {
          this.fetchPlatformInfoState = asyncDataFailed({});
        });
      }
    } catch (error) {
      logger.error({
        message: "Failed to fetch platform info for the current account.",
        info: {
          error: objectModule.safeErrorAsJson(error as Error),
        },
      });

      runInAction(() => {
        this.fetchPlatformInfoState = asyncDataFailed({});
      });
    }

    // Persist feature gate configs (data-only) for offline use
    try {
      const plainObj = Object.fromEntries(toJS(this.featureGateConfigs)) as GatedFeatureConfigMap;
      const payload = buildSerializableGatedFeatureConfigMap(plainObj);
      await this.store.accountCache.set(CacheKey.FeatureGateConfigs, payload);
    } catch (unknownErr) {
      logger.warn({
        message: "[PlatformInfo.fetchPlatformInfo] Failed to persist feature gate configs",
        info: { err: objectModule.safeErrorAsJson(unknownErr as BaseError) },
      });
    }

    await this.persistBillingSubscriptionToCache();
    await this.persistEntitlementsToCache();

    return didFetchPlatformInfo;
  }

  private async attemptCommercialCatalogEnrollmentOnce() {
    if (this.commercialCatalogEnrollmentAttempted) {
      return;
    }

    if (this.commercialCatalogEnrollmentPromise) {
      return this.commercialCatalogEnrollmentPromise;
    }

    this.commercialCatalogEnrollmentPromise = (async () => {
      try {
        const response = await api.post("/v2/me/commercial-catalog/enrollment");
        if (response?.data) {
          this.commercialCatalogEnrollmentAttempted = true;
        } else {
          logger.warn({
            message: "Commercial catalog enrollment attempt was not accepted.",
            info: { response: objectModule.safeAsJson(response) },
          });
        }
      } catch (error) {
        logger.warn({
          message: "Commercial catalog enrollment attempt failed.",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      } finally {
        this.commercialCatalogEnrollmentPromise = null;
      }
    })();

    return this.commercialCatalogEnrollmentPromise;
  }

  async refreshPlatformInfoAfterInvalidation() {
    this.platformInfoInvalidationRefreshQueued = true;

    if (this.platformInfoInvalidationRefreshPromise) {
      return this.platformInfoInvalidationRefreshPromise;
    }

    this.platformInfoInvalidationRefreshPromise = this.flushPlatformInfoInvalidationRefreshes();

    return this.platformInfoInvalidationRefreshPromise;
  }

  private async flushPlatformInfoInvalidationRefreshes() {
    try {
      while (this.platformInfoInvalidationRefreshQueued) {
        this.platformInfoInvalidationRefreshQueued = false;
        await this.fetchPlatformInfo();
      }
    } finally {
      this.platformInfoInvalidationRefreshPromise = null;
    }
  }

  async dismissIcpRfi({ completedSteps }: { completedSteps: string[] }) {
    // Optimistically dismiss the ICP RFI immediately
    runInAction(() => {
      this.onboardingInfo.shouldShowIcpRfi = false;
      if (completedSteps) {
        completedSteps.forEach(step => this.onboardingInfo.markTaskCompleted(step));
      }
    });

    await pRetry(async () => {
      const response = await api.post("/v2/me/platform-info/dismiss-icp-rfi");

      if (response?.data?.onboarding_info) {
        // The API endpoint is returning `[key: string]: unknown`...
        // But it should match the `PlatformInfoOnboarding` response type.
        const apiOnboardingInfo = response.data
          .onboarding_info as ApiSchema["PlatformInfoOnboarding"];

        this.updateOnboardingInfoFromRemoteData({ data: apiOnboardingInfo });
        return;
      }

      if (response?.error) {
        const err = new BaseError({
          message: "Failed to dismiss ICP RFI for current account.",
          info: {
            response: objectModule.safeAsJson(response),
          },
        });
        throw err;
      }
    });
  }

  async dismissHeadsUpCoachMark() {
    runInAction(() => {
      this.onboardingInfo.shouldShowHeadsUpCoachMark = false;
    });

    await pRetry(async () => {
      const response = await api.post("/v2/me/platform-info/dismiss-heads-up-coach-mark", {});

      if (response?.data?.onboarding_info) {
        // The API endpoint is returning `[key: string]: unknown`...
        // But it should match the `PlatformInfoOnboarding` response type.
        const apiOnboardingInfo = response.data
          .onboarding_info as ApiSchema["PlatformInfoOnboarding"];

        this.updateOnboardingInfoFromRemoteData({ data: apiOnboardingInfo });
        return;
      }

      if (response?.error) {
        const err = new BaseError({
          message: "Failed to dismiss Heads Up Coach Mark for current account.",
          info: {
            response: objectModule.safeAsJson(response),
          },
        });
        throw err;
      }
    });
  }

  updateOnboardingInfoFromRemoteData({ data }: { data: ApiSchema["PlatformInfoOnboarding"] }) {
    runInAction(() => {
      const updatedOnboardingInfo = PlatformOnboardingInfoObservable.createFromRemoteData({
        data,
        store: this.store,
        perDeviceOnboardingInfo: this.perDeviceOnboardingInfo,
      });
      this.onboardingInfo.shouldShowIcpRfi = updatedOnboardingInfo.shouldShowIcpRfi;
      this.onboardingInfo.shouldShowHeadsUpCoachMark =
        updatedOnboardingInfo.shouldShowHeadsUpCoachMark;
      this.onboardingInfo.completedTaskIds = updatedOnboardingInfo.completedTaskIds;
      this.onboardingInfo.allTasksCompletedAt = updatedOnboardingInfo.allTasksCompletedAt;
      this.onboardingInfo.onboardingFlowVersion = updatedOnboardingInfo.onboardingFlowVersion;
      this.onboardingInfo.onboardingIntent = updatedOnboardingInfo.onboardingIntent;
      this.onboardingInfo.intentOnboardingCompletedAt =
        updatedOnboardingInfo.intentOnboardingCompletedAt;
    });
  }

  async assignIntentOnboardingIfEligible() {
    // Mobile web keeps the established App Store handoff. Check this before
    // desktop-family user-agent helpers because some iPads identify as macOS.
    if (clientEnvModule.isMobileBrowser() || clientEnvModule.isIosBrowser()) {
      return;
    }
    const platform = clientEnvModule.isMacBrowser()
      ? "mac"
      : clientEnvModule.isWindowsBrowser()
        ? "windows"
        : null;
    if (!platform || clientEnvModule.isDesktop() || this.onboardingInfo.allTasksCompletedAt) {
      return;
    }

    const account = this.store.account.myAccount;
    const hasFreshWebSignupReceipt = localStorageModule.hasIntentOnboardingSignupReceipt({
      accountId: account.id,
      email: account.profileEmailAddress,
    });
    if (!hasFreshWebSignupReceipt) {
      return;
    }

    try {
      const response = await api.post("/v2/me/platform-info/intent-onboarding/assign", {
        body: {
          platform,
          surface: "web",
          has_fresh_web_signup_receipt: true,
        },
      });
      if (response.data?.onboarding_info) {
        this.updateOnboardingInfoFromRemoteData({
          data: response.data.onboarding_info,
        });
        if (this.onboardingInfo.onboardingFlowVersion === "intent_v1") {
          posthogClientManager.setPersonProperties(
            { onboarding_flow_version: "intent_v1" },
            { onboarding_flow_version: "intent_v1" }
          );
        }
      }
    } catch (error) {
      // Assignment is fail-closed. A transient PostHog or API failure leaves
      // the person on the established onboarding flow.
      logger.warn({
        message: "[PlatformInfo] Intent onboarding assignment failed closed",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    } finally {
      // This claim is deliberately one-shot. A person evaluated while the
      // kill switch is off stays on legacy rather than being admitted later
      // by a reload after the flag changes.
      localStorageModule.clearIntentOnboardingSignupReceipt();
    }
  }

  async setIntentOnboardingIntent(intent: "meetings" | "quick_thoughts") {
    const response = await api.post("/v2/me/platform-info/intent-onboarding/intent", {
      body: { intent },
    });
    if (!response.data?.onboarding_info) {
      throw new BaseError({ message: "Failed to persist onboarding intent." });
    }
    this.updateOnboardingInfoFromRemoteData({
      data: response.data.onboarding_info,
    });
    posthogClientManager.setPersonProperties({ onboarding_intent: intent });
  }

  async completeIntentOnboarding() {
    const response = await api.post("/v2/me/platform-info/intent-onboarding/complete");
    if (!response.data?.onboarding_info) {
      throw new BaseError({ message: "Failed to complete intent onboarding." });
    }
    this.updateOnboardingInfoFromRemoteData({
      data: response.data.onboarding_info,
    });
    posthogClientManager.setPersonProperties({
      intent_onboarding_completed: true,
      intent_onboarding_completed_at:
        this.onboardingInfo.intentOnboardingCompletedAt ?? new Date().toISOString(),
    });
  }

  updateSubscriptionFromRemoteData({ data }: { data: BillingSubscriptionData }) {
    this.subscription = data;
  }

  updateEntitlementsFromRemoteData({ data }: { data: PlatformEntitlementsData }) {
    this.entitlements = data;
  }

  updateFeatureGateConfigsFromRemoteData({ data }: { data: GatedFeatureConfigMapData }) {
    const fullConfig = buildGatedFeatureConfigMapFromData(data ?? {});
    this.featureGateConfigs.clear();
    for (const [feature, config] of Object.entries(fullConfig)) {
      this.featureGateConfigs.set(feature as GatedFeature, config);
    }
  }

  getFeatureGateConfig(gateId: GatedFeature): GatedFeatureConfig | undefined {
    return this.featureGateConfigs.get(gateId);
  }

  async persistBillingSubscriptionToCache(): Promise<boolean> {
    if (!this.subscription) {
      return false;
    }

    try {
      return await this.store.accountCache.set(CacheKey.BillingSubscription, this.subscription);
    } catch (unknownErr) {
      logger.warn({
        message: "[PlatformInfo.persistBillingSubscriptionToCache] Failed to persist subscription",
        info: { err: objectModule.safeErrorAsJson(unknownErr as BaseError) },
      });
      return false;
    }
  }

  async hydrateBillingSubscriptionFromCache(): Promise<boolean> {
    try {
      const cachedSubscription = await this.store.accountCache.get(CacheKey.BillingSubscription);
      if (!cachedSubscription) {
        return false;
      }

      runInAction(() => {
        this.updateSubscriptionFromRemoteData({
          data: cachedSubscription as BillingSubscriptionData,
        });
      });

      return true;
    } catch (unknownErr) {
      logger.warn({
        message: "[PlatformInfo.hydrateBillingSubscriptionFromCache] Failed to hydrate from cache",
        info: { err: objectModule.safeErrorAsJson(unknownErr as BaseError) },
      });
      return false;
    }
  }

  async persistEntitlementsToCache(): Promise<boolean> {
    if (!this.entitlements) {
      return false;
    }

    try {
      return await this.store.accountCache.set(CacheKey.PlatformEntitlements, this.entitlements);
    } catch (unknownErr) {
      logger.warn({
        message: "[PlatformInfo.persistEntitlementsToCache] Failed to persist entitlements",
        info: { err: objectModule.safeErrorAsJson(unknownErr as BaseError) },
      });
      return false;
    }
  }

  async hydrateEntitlementsFromCache(): Promise<boolean> {
    try {
      const cachedEntitlements = await this.store.accountCache.get(CacheKey.PlatformEntitlements);
      if (!cachedEntitlements) {
        return false;
      }

      runInAction(() => {
        this.updateEntitlementsFromRemoteData({
          data: cachedEntitlements as PlatformEntitlementsData,
        });
      });

      return true;
    } catch (unknownErr) {
      logger.warn({
        message:
          "[PlatformInfo.hydrateEntitlementsFromCache] Failed to hydrate entitlements from cache",
        info: { err: objectModule.safeErrorAsJson(unknownErr as BaseError) },
      });
      return false;
    }
  }

  async hydrateFeatureGateConfigsFromCache(): Promise<boolean> {
    try {
      const cachedConfigs = await this.store.accountCache.get(CacheKey.FeatureGateConfigs);
      if (!cachedConfigs) {
        return false;
      }

      runInAction(() => {
        this.updateFeatureGateConfigsFromRemoteData({
          data: cachedConfigs as GatedFeatureConfigMapData,
        });
      });

      return true;
    } catch (unknownErr) {
      logger.warn({
        message: "[PlatformInfo.hydrateFeatureGateConfigsFromCache] Failed to hydrate from cache",
        info: { err: objectModule.safeErrorAsJson(unknownErr as BaseError) },
      });
      return false;
    }
  }

  async initialize() {
    // This hydrates any device-related data from our local cache
    await this.perDeviceOnboardingInfo.initialize();

    // Kick off network fetch
    const fetchPromise = this.fetchPlatformInfo();

    // Try to hydrate from cache for offline/fast startup
    const [
      featureGateHydrationSuccess,
      billingSubscriptionHydrationSuccess,
      entitlementsHydrationSuccess,
    ] = await Promise.all([
      this.hydrateFeatureGateConfigsFromCache(),
      this.hydrateBillingSubscriptionFromCache(),
      this.hydrateEntitlementsFromCache(),
    ]);

    if (featureGateHydrationSuccess) {
      logger.info({
        message: "[PlatformInfo.initialize] Successfully hydrated feature gate configs from cache",
      });
    }

    if (billingSubscriptionHydrationSuccess) {
      logger.info({
        message: "[PlatformInfo.initialize] Successfully hydrated billing subscription from cache",
      });
    }

    if (entitlementsHydrationSuccess) {
      logger.info({
        message: "[PlatformInfo.initialize] Successfully hydrated entitlements from cache",
      });
    }

    await fetchPromise;
  }
}
